import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';

import PbxContext from '../../../context/PbxContext';
import { accessLocalStorage } from '../../../services';

import Loading from '../../../components/Loading/LoadingModule';

import '../../../libs/bulma.min.css';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faArrowLeft, faCopy, faCheck } from '@fortawesome/free-solid-svg-icons';

function IntegrationsView() {
  const getItensStateGlobal = useContext(PbxContext);
  const { itemsSelectedToEdit, toggleIsChangeFormElements, toggleIsHidden, setItemsSelectedToEdit } = getItensStateGlobal;

  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [copiedField, setCopiedField] = useState(null);

  const validateUserLogged = useCallback(async () => {
    const dataUser = await accessLocalStorage.getUserLocalStorage();
    if (!dataUser) return history.push('/');
    if (dataUser && dataUser.user?.role !== 'root') return history.push('/home');
    
    // Verificar se há dados para visualizar
    if (!itemsSelectedToEdit || !itemsSelectedToEdit.id_integration) {
      return history.push('/config/integracoes/lista');
    }

    setLoading(false);
  }, [history, itemsSelectedToEdit]);

  useEffect(() => {
    validateUserLogged();
  }, [validateUserLogged]);

  const handleEdit = () => {
    toggleIsChangeFormElements();
    toggleIsHidden();
    history.push('/config/integracoes/editar');
  };

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Erro ao copiar para área de transferência:', err);
    }
  };

  const formatType = (type) => {
    const typeMap = {
      'whatsapp': 'WhatsApp',
      'sms': 'SMS',
      'webhook': 'Webhook',
      'noah': 'Noah',
      'sgcon': 'Sgcon',
      'api': 'API'
    };
    return typeMap[type?.toLowerCase()] || type;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTypeColor = (type) => {
    const colorMap = {
      'whatsapp': 'is-success',
      'sms': 'is-info',
      'webhook': 'is-warning',
      'noah': 'is-primary',
      'sgcon': 'is-link',
      'api': 'is-dark'
    };
    return colorMap[type?.toLowerCase()] || 'is-light';
  };

  if (loading) return <Loading />;

  const integration = itemsSelectedToEdit;

  return (
    <div className="container is-fluid">
      <div className="columns">
        <div className="column is-half is-offset-one-quarter has-text-centered">
          <strong className="is-size-3">VISUALIZAR INTEGRAÇÃO</strong>
          <hr className="dropdown-divider"/>
        </div>
      </div>
      <div className="columns is-centered">
        <div className="column is-10">
          <div className="box">
            <div className="level">
              <div className="level-left">
                <div className="level-item">
                  <h1 className="title is-4">
                    <FontAwesomeIcon icon={faEye} className="mr-2" />
                    Visualizar Integração #{integration.id_integration}
                  </h1>
                </div>
              </div>
              <div className="level-right">
                <div className="level-item">
                  <div className="buttons">
                    <button 
                      className="button is-warning"
                      onClick={handleEdit}
                    >
                      <FontAwesomeIcon icon={faPen} className="mr-2" />
                      Editar
                    </button>
                    <Link to="/config/integracoes/lista" className="button is-light">
                      <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                      Voltar
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="columns">
              <div className="column is-8">
                {/* Informações Principais */}
                <div className="box">
                  <h2 className="title is-5">📋 Informações Gerais</h2>
                  
                  <div className="field">
                    <label className="label">ID da Integração</label>
                    <div className="control has-icons-right">
                      <input 
                        className="input" 
                        type="text" 
                        value={`#${integration.id_integration}`}
                        readOnly 
                      />
                      <span className="icon is-small is-right">
                        <button 
                          className="button is-small is-ghost"
                          onClick={() => copyToClipboard(integration.id_integration.toString(), 'id')}
                        >
                          <FontAwesomeIcon 
                            icon={copiedField === 'id' ? faCheck : faCopy} 
                            className={copiedField === 'id' ? 'has-text-success' : 'has-text-grey'}
                          />
                        </button>
                      </span>
                    </div>
                  </div>

                  <div className="columns">
                    <div className="column">
                      <div className="field">
                        <label className="label">Account Code</label>
                        <div className="control has-icons-right">
                          <input 
                            className="input" 
                            type="text" 
                            value={integration.accountcode || ''}
                            readOnly 
                          />
                          <span className="icon is-small is-right">
                            <button 
                              className="button is-small is-ghost"
                              onClick={() => copyToClipboard(integration.accountcode, 'accountcode')}
                            >
                              <FontAwesomeIcon 
                                icon={copiedField === 'accountcode' ? faCheck : faCopy} 
                                className={copiedField === 'accountcode' ? 'has-text-success' : 'has-text-grey'}
                              />
                            </button>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="column">
                      <div className="field">
                        <label className="label">Tipo</label>
                        <div className="control">
                          <span className={`tag is-large ${getTypeColor(integration.type)}`}>
                            {formatType(integration.type)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* URL da API */}
                <div className="box">
                  <h2 className="title is-5">🔗 URL da API</h2>
                  <div className="field">
                    <div className="control has-icons-right">
                      <input 
                        className="input" 
                        type="url" 
                        value={integration.url || ''}
                        readOnly 
                      />
                      <span className="icon is-small is-right">
                        <button 
                          className="button is-small is-ghost"
                          onClick={() => copyToClipboard(integration.url, 'url')}
                        >
                          <FontAwesomeIcon 
                            icon={copiedField === 'url' ? faCheck : faCopy} 
                            className={copiedField === 'url' ? 'has-text-success' : 'has-text-grey'}
                          />
                        </button>
                      </span>
                    </div>
                    <p className="help">
                      Clique no ícone para copiar a URL
                    </p>
                  </div>
                </div>

                {/* Token */}
                <div className="box">
                  <h2 className="title is-5">🔐 Token de Autenticação</h2>
                  <div className="field">
                    <div className="control has-icons-right">
                      <textarea 
                        className="textarea" 
                        rows="4"
                        value={integration.token || ''}
                        readOnly 
                        style={{ fontFamily: 'monospace', fontSize: '0.875rem' }}
                      />
                      <span className="icon is-small is-right" style={{ top: '10px' }}>
                        <button 
                          className="button is-small is-ghost"
                          onClick={() => copyToClipboard(integration.token, 'token')}
                        >
                          <FontAwesomeIcon 
                            icon={copiedField === 'token' ? faCheck : faCopy} 
                            className={copiedField === 'token' ? 'has-text-success' : 'has-text-grey'}
                          />
                        </button>
                      </span>
                    </div>
                    <p className="help has-text-warning">
                      ⚠️ Mantenha este token seguro e não compartilhe com terceiros
                    </p>
                  </div>
                </div>

                {/* Mensagem */}
                {integration.message && (
                  <div className="box">
                    <h2 className="title is-5">💬 Mensagem</h2>
                    <div className="field">
                      <div className="control has-icons-right">
                        <textarea 
                          className="textarea" 
                          rows="3"
                          value={integration.message || ''}
                          readOnly 
                        />
                        <span className="icon is-small is-right" style={{ top: '10px' }}>
                          <button 
                            className="button is-small is-ghost"
                            onClick={() => copyToClipboard(integration.message, 'message')}
                          >
                            <FontAwesomeIcon 
                              icon={copiedField === 'message' ? faCheck : faCopy} 
                              className={copiedField === 'message' ? 'has-text-success' : 'has-text-grey'}
                            />
                          </button>
                        </span>
                      </div>
                      <p className="help">
                        Mensagem descritiva sobre esta integração ({integration.message?.length || 0}/280 caracteres)
                      </p>
                    </div>
                  </div>
                )}

                {integration.template_name && (
                  <div className="box">
                    <h2 className="title is-5">Template WhatsApp Cloud</h2>
                    <div className="columns">
                      <div className="column">
                        <div className="field">
                          <label className="label">Nome do Template</label>
                          <div className="control has-icons-right">
                            <input 
                              className="input" 
                              type="text" 
                              value={integration.template_name || ''}
                              readOnly 
                            />
                            <span className="icon is-small is-right">
                              <button 
                                className="button is-small is-ghost"
                                onClick={() => copyToClipboard(integration.template_name, 'template_name')}
                              >
                                <FontAwesomeIcon 
                                  icon={copiedField === 'template_name' ? faCheck : faCopy} 
                                  className={copiedField === 'template_name' ? 'has-text-success' : 'has-text-grey'}
                                />
                              </button>
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="column">
                        <div className="field">
                          <label className="label">Idioma</label>
                          <div className="control has-icons-right">
                            <input 
                              className="input" 
                              type="text" 
                              value={integration.template_language || 'pt_BR'}
                              readOnly 
                            />
                            <span className="icon is-small is-right">
                              <button 
                                className="button is-small is-ghost"
                                onClick={() => copyToClipboard(integration.template_language || 'pt_BR', 'template_language')}
                              >
                                <FontAwesomeIcon 
                                  icon={copiedField === 'template_language' ? faCheck : faCopy} 
                                  className={copiedField === 'template_language' ? 'has-text-success' : 'has-text-grey'}
                                />
                              </button>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="column is-4">
                {/* Informações de Sistema */}
                <div className="box">
                  <h2 className="title is-5">⏰ Timestamps</h2>
                  
                  <div className="field">
                    <label className="label">Criado em</label>
                    <div className="control">
                      <input 
                        className="input is-small" 
                        type="text" 
                        value={formatDate(integration.created_at)}
                        readOnly 
                      />
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Última atualização</label>
                    <div className="control">
                      <input 
                        className="input is-small" 
                        type="text" 
                        value={formatDate(integration.updated_at)}
                        readOnly 
                      />
                    </div>
                  </div>
                </div>

                {/* Status e Validação */}
                <div className="box">
                  <h2 className="title is-5">✅ Status</h2>
                  
                  <div className="field">
                    <label className="label">URL Válida</label>
                    <div className="control">
                      <span className="tag is-success">
                        ✓ URL formatada corretamente
                      </span>
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Token</label>
                    <div className="control">
                      <span className="tag is-info">
                        {integration.token?.length > 0 ? `${integration.token.length} caracteres` : 'Não definido'}
                      </span>
                    </div>
                  </div>

                  <div className="field">
                    <label className="label">Mensagem</label>
                    <div className="control">
                      <span className={`tag ${integration.message && integration.message.length > 0 ? 'is-success' : 'is-light'}`}>
                        {integration.message && integration.message.length > 0 
                          ? `${integration.message.length} caracteres` 
                          : 'Sem mensagem'
                        }
                      </span>
                    </div>
                  </div>
                </div>

                {/* Ações Rápidas */}
                <div className="box">
                  <h2 className="title is-5">⚡ Ações Rápidas</h2>
                  
                  <div className="buttons">
                    <button 
                      className="button is-warning is-fullwidth"
                      onClick={handleEdit}
                    >
                      <FontAwesomeIcon icon={faPen} className="mr-2" />
                      Editar Integração
                    </button>
                  </div>

                  <div className="buttons">
                    <button 
                      className="button is-light is-fullwidth"
                      onClick={() => copyToClipboard(integration.url, 'url-quick')}
                    >
                      <FontAwesomeIcon icon={faCopy} className="mr-2" />
                      Copiar URL
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notificação de Cópia */}
      {copiedField && (
        <div className="notification is-success is-light" style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 1000,
          minWidth: '250px'
        }}>
          <FontAwesomeIcon icon={faCheck} className="mr-2" />
          Copiado para área de transferência!
        </div>
      )}
    </div>
  );
}

export default IntegrationsView;
